// Full-page loading skeleton for /capacities. Used as the route's chunk-load
// fallback (in AppRouter) and rendered server-side by the matching Phlex
// partial — keep both in sync so React hydration is byte-aligned and the
// shimmer animation doesn't restart.
//
// Keep in sync with app/components/skeletons/capacities.rb.

import { Bar, DEFAULT_LABEL } from './primitives';
import { CapacitiesSectionSkeleton, CAPACITIES_BAND_ROW_COUNT, CAPACITIES_SLOT_ROW_COUNT } from './CapacitiesSkeleton';

// Matches `CapacityManagement/index.tsx`'s default `toolbarHeight`. The shimmer
// toolbar below uses identical `py-4` + `h-12` so the sticky offset lines up.
const TOOLBAR_HEIGHT_PX = 69;

export interface CapacitiesPageSkeletonProps {
  label?: string;
}

export function CapacitiesPageSkeleton({ label = DEFAULT_LABEL }: CapacitiesPageSkeletonProps) {
  return (
    <div role="status" aria-busy="true" aria-label={label}>
      <div className="flex flex-col h-screen overflow-hidden bg-off-white">
        <header className="flex shrink-0 items-center h-23 border-b border-dinnerplate-white">
          <div className="px-16">
            <Bar className="h-5 w-48" />
          </div>
        </header>

        <div className="capacities-scroll-container relative flex min-h-0 flex-1 flex-col gap-0 overflow-x-clip overflow-y-scroll px-16 pt-0 pb-8">
          {/* Fixed-position vertical divider — `CapacityManagement` computes
              the inset dynamically; the skeleton hard-codes `right-6` (= 18px,
              the scrollbar gutter width) so the divider doesn't appear/jump on
              the skeleton → real swap. A `border-l-thin` (= 0.5px) on a
              0-width fixed element renders the same hairline as the real
              divider's `w-[0.5px] bg-dinnerplate-white`. Keep in sync with
              app/components/skeletons/capacities.rb. */}
          <div
            aria-hidden="true"
            className="pointer-events-none fixed top-23 bottom-23 right-6 z-50 border-l-thin border-dinnerplate-white"
          />
          <div className="pt-8">
            <Bar className="h-12 w-48" />
          </div>

          <div className="sticky top-0 z-40 shrink-0 bg-off-white py-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <Bar className="h-12 w-117 rounded-input" />
              <Bar className="h-12 w-32 rounded-input" />
            </div>
          </div>

          <div className="flex min-w-0 flex-col [&>*+*]:mt-4">
            <CapacitiesSectionSkeleton
              titleWidthClass="w-32"
              rowCount={CAPACITIES_BAND_ROW_COUNT}
              stickyTopPx={TOOLBAR_HEIGHT_PX}
            />
            <CapacitiesSectionSkeleton
              titleWidthClass="w-28"
              rowCount={CAPACITIES_SLOT_ROW_COUNT}
              stickyTopPx={TOOLBAR_HEIGHT_PX}
            />
          </div>
        </div>

        <footer className="flex-shrink-0 h-23 border-t border-dinnerplate-white">
          <div className="flex h-full w-full items-center justify-end px-16">
            <div className="flex items-center gap-4">
              <Bar className="h-12 w-20 rounded-input" />
              <Bar className="h-12 w-32 rounded-input" />
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
